/**
 * Content Engine Health Widget — v4.36 admin operations dashboard
 *
 * One-glance signal for the content engine over the last 7 days:
 *   • Watchdog reset count (was anything stuck and auto-cleared?)
 *   • Per-platform validator rejection rate (which prompts are degrading?)
 *   • Average end-to-end run duration (is generation slowing down?)
 *
 * Pure read endpoint. No actions — admins pivot from this widget into
 * specific package editors when a metric looks off.
 */
import { useEffect, useState } from "react";
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Clock,
    Code,
    Loader2,
    RefreshCw,
    ShieldAlert,
    XCircle,
    X,
    PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    getAdminContentEngineHealth,
    getAdminContentEnginePlatformFailures,
    adminContentEnginePlatformRetryRecent,
} from "@/lib/api";

const PLATFORM_LABELS = {
    pinterest: "Pinterest",
    instagram: "Instagram",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    x: "X",
    email: "Email",
    video: "Video",
};

function formatDuration(ms) {
    if (!ms || ms <= 0) return "—";
    if (ms < 1000) return `${ms} ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)} s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
}

function rejectionTint(ratePct) {
    if (ratePct >= 25) return "text-[var(--bl-rust)]";
    if (ratePct >= 10) return "text-[var(--bl-amber)]";
    return "text-bl-muted";
}

export default function ContentEngineHealthWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [drillPlatform, setDrillPlatform] = useState(null);

    const load = async () => {
        try {
            const d = await getAdminContentEngineHealth();
            setData(d);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to load");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    if (loading) {
        return (
            <div
                className="bl-card p-5 flex items-center gap-2 text-sm text-bl-muted"
                data-testid="ce-health-loading"
            >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading content engine health…
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="bl-card p-5 border-[var(--bl-rust)] bg-[var(--bl-rust-tint,#FBE9E1)]/30"
                data-testid="ce-health-error"
            >
                <div className="flex items-center gap-2 text-sm text-[var(--bl-rust)]">
                    <XCircle className="h-4 w-4" />
                    Health snapshot unavailable: {error}
                </div>
            </div>
        );
    }

    const totalOutcomes =
        (data?.completion_outcomes?.complete || 0)
        + (data?.completion_outcomes?.complete_with_errors || 0)
        + (data?.completion_outcomes?.failed || 0);

    // Active channels only. Locked platforms (FB / IG / LinkedIn / X /
    // legacy video) still report historical rejection data from before
    // the channel-lock landed; we hide them so the rejection-rate chart
    // doesn't surface bars that the admin can no longer act on.
    const ACTIVE_PLATFORM_KEYS = new Set([
        "pinterest", "email",
        "youtube_long_form", "youtube_shorts_batch", "youtube_thumbnail",
    ]);
    const platforms = Object.entries(data?.per_platform || {}).filter(
        ([key]) => ACTIVE_PLATFORM_KEYS.has(key),
    );
    const hotPlatforms = platforms.filter(
        ([, v]) => (v.rejection_rate_pct || 0) >= 10,
    );

    return (
        <section
            className="bl-card p-5 sm:p-6"
            data-testid="ce-health-widget"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="bl-eyebrow mb-1 flex items-center gap-1.5">
                        <Activity className="h-3 w-3" />
                        Content engine health
                    </div>
                    <h2 className="font-display font-semibold text-bl text-lg mt-1">
                        Last 7 days
                    </h2>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setRefreshing(true);
                        load();
                    }}
                    disabled={refreshing}
                    data-testid="ce-health-refresh"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-bl-muted hover:text-bl hover:bg-bl-bg disabled:opacity-50 transition-colors"
                    title="Refresh"
                >
                    {refreshing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    Refresh
                </button>
            </div>

            {/* KPI row */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <KPI
                    icon={ShieldAlert}
                    label="Watchdog resets"
                    value={data.watchdog_resets_7d}
                    sublabel={data.watchdog_resets_7d > 0 ? "auto-unstuck" : "none"}
                    tint={data.watchdog_resets_7d > 0 ? "amber" : "muted"}
                    testid="ce-health-watchdog"
                />
                <KPI
                    icon={CheckCircle2}
                    label="Completion rate"
                    value={`${data.completion_rate_pct || 0}%`}
                    sublabel={`${data.completed_runs_7d || 0} runs`}
                    tint={
                        data.completion_rate_pct >= 95 ? "green"
                        : data.completion_rate_pct >= 80 ? "amber"
                        : data.completed_runs_7d > 0 ? "rust" : "muted"
                    }
                    testid="ce-health-completion"
                />
                <KPI
                    icon={Clock}
                    label="Avg run duration"
                    value={formatDuration(data.avg_duration_ms_7d)}
                    sublabel="end-to-end"
                    tint="muted"
                    testid="ce-health-duration"
                />
                <KPI
                    icon={AlertTriangle}
                    label="Hot platforms"
                    value={hotPlatforms.length}
                    sublabel={hotPlatforms.length > 0 ? "≥10% rejected · click to retry" : "all healthy"}
                    tint={hotPlatforms.length > 0 ? "rust" : "green"}
                    testid="ce-health-hot"
                    onClick={
                        hotPlatforms.length > 0
                            ? () => setDrillPlatform(hotPlatforms[0][0])
                            : null
                    }
                />
            </div>

            {/* Outcomes pill row */}
            {totalOutcomes > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-bl-muted uppercase tracking-[0.14em] text-[10px]">
                        Run outcomes:
                    </span>
                    <OutcomePill
                        label="Clean"
                        value={data.completion_outcomes?.complete || 0}
                        tint="bg-[var(--bl-green-soft)]/40 text-[var(--bl-green)]"
                    />
                    <OutcomePill
                        label="Partial"
                        value={data.completion_outcomes?.complete_with_errors || 0}
                        tint="bg-[var(--bl-amber-soft)]/40 text-[var(--bl-amber)]"
                    />
                    <OutcomePill
                        label="Failed"
                        value={data.completion_outcomes?.failed || 0}
                        tint="bg-[var(--bl-rust-tint,#FBE9E1)]/40 text-[var(--bl-rust)]"
                    />
                </div>
            )}

            {/* Per-platform breakdown */}
            <div className="mt-5">
                <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium mb-2">
                    Per-platform validator rejection rate
                </div>
                <div className="space-y-1.5" data-testid="ce-health-platforms">
                    {platforms.map(([platform, stats]) => (
                        <PlatformRow
                            key={platform}
                            platform={platform}
                            stats={stats}
                            onClick={
                                (stats.rejections || 0) > 0
                                    ? () => setDrillPlatform(platform)
                                    : null
                            }
                        />
                    ))}
                </div>
                <p className="mt-3 text-[11px] text-bl-muted leading-relaxed">
                    Each generation run attempts all 7 platforms. A high rejection rate signals
                    a degrading prompt or a flaky LLM provider — click any platform with rejections
                    to see recent failures and re-run them in one click.
                </p>
            </div>
            {drillPlatform && (
                <PlatformFailuresModal
                    platform={drillPlatform}
                    onClose={() => setDrillPlatform(null)}
                    onAfterRetry={() => {
                        // Re-pull health after a retry queues so admins see
                        // the updated rejection counts.
                        setRefreshing(true);
                        load();
                    }}
                />
            )}
        </section>
    );
}

function KPI({ icon: Icon, label, value, sublabel, tint, testid, onClick }) {
    const tintClasses = {
        green: "border-[var(--bl-green)] bg-[var(--bl-green-soft)]/30 text-[var(--bl-green)]",
        amber: "border-[var(--bl-amber)] bg-[var(--bl-amber-soft)]/30 text-[var(--bl-amber)]",
        rust:  "border-[var(--bl-rust)] bg-[var(--bl-rust-tint,#FBE9E1)]/30 text-[var(--bl-rust)]",
        muted: "border-bl bg-bl-bg/30 text-bl-muted",
    };
    const cls = tintClasses[tint] || tintClasses.muted;
    const inner = (
        <>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] tracking-[0.16em] uppercase font-medium opacity-80">
                    {label}
                </span>
                <Icon className="h-3.5 w-3.5 opacity-70" />
            </div>
            <div className="mt-1 font-display font-semibold text-xl text-bl">
                {value}
            </div>
            <div className="text-[11px] opacity-80">{sublabel}</div>
        </>
    );
    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                className={"text-left rounded-lg border px-3 py-2.5 transition-all hover:shadow-md hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-[var(--bl-teal-tint)] " + cls}
                data-testid={testid}
                title="Click to see recent failures + retry"
            >
                {inner}
            </button>
        );
    }
    return (
        <div
            className={"rounded-lg border px-3 py-2.5 " + cls}
            data-testid={testid}
        >
            {inner}
        </div>
    );
}

function OutcomePill({ label, value, tint }) {
    return (
        <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium " + tint}>
            <span className="font-mono-bl">{value}</span>
            {label}
        </span>
    );
}

function PlatformRow({ platform, stats, onClick }) {
    const label = PLATFORM_LABELS[platform] || platform;
    const rate = stats.rejection_rate_pct || 0;
    const width = Math.min(100, Math.max(rate, rate > 0 ? 4 : 0));
    const tint =
        rate >= 25 ? "bg-[var(--bl-rust)]"
        : rate >= 10 ? "bg-[var(--bl-amber)]"
        : "bg-[var(--bl-teal-tint)]";
    const row = (
        <>
            <div className="w-20 text-bl font-medium">{label}</div>
            <div className="flex-1 h-2 rounded-full bg-bl-bg overflow-hidden">
                <div
                    className={"h-full " + tint + " transition-all duration-500"}
                    style={{ width: `${width}%` }}
                />
            </div>
            <div className={"w-20 text-right font-mono-bl text-xs " + rejectionTint(rate)}>
                {rate}%
                <span className="text-bl-muted ml-1">
                    ({stats.rejections}/{stats.attempts})
                </span>
            </div>
        </>
    );
    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                className="w-full flex items-center gap-3 text-xs hover:bg-bl-bg/40 -mx-2 px-2 py-0.5 rounded transition-colors focus:outline-none focus:ring-1 focus:ring-[var(--bl-teal-tint)]"
                data-testid={`ce-health-platform-${platform}`}
                title={`See recent ${label} failures + retry`}
            >
                {row}
            </button>
        );
    }
    return (
        <div
            className="flex items-center gap-3 text-xs"
            data-testid={`ce-health-platform-${platform}`}
        >
            {row}
        </div>
    );
}

// ─────────── Platform failures drill-down modal ───────────

const PLATFORM_LABELS_MODAL = PLATFORM_LABELS;

function PlatformFailuresModal({ platform, onClose, onAfterRetry }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getAdminContentEnginePlatformFailures(platform, { limit: 25 })
            .then((d) => {
                if (!cancelled) setData(d);
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.response?.data?.detail || e?.message || "Load failed");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [platform]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const handleRetryTopN = async (n) => {
        const pkgs = (data?.unique_packages || []).slice(0, n);
        if (pkgs.length === 0) {
            toast.error("Nothing to retry.");
            return;
        }
        setRetrying(true);
        try {
            const res = await adminContentEnginePlatformRetryRecent(
                platform, pkgs.map((p) => p.package_id),
            );
            const q = res?.queued_count || 0;
            if (q > 0) {
                toast.success(
                    `Re-running ${PLATFORM_LABELS_MODAL[platform] || platform} on ${q} package${q === 1 ? "" : "s"}. Check back in a minute — the Health card will refresh.`,
                );
                onAfterRetry?.();
            } else {
                toast.error("No packages were queued — check the skipped list.");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retry failed.");
        } finally {
            setRetrying(false);
        }
    };

    const label = PLATFORM_LABELS_MODAL[platform] || platform;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
            onClick={onClose}
            data-testid="ce-health-failures-modal"
        >
            <div
                className="bl-card bg-white max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-bl">
                    <div>
                        <div className="bl-eyebrow mb-1">Platform failures</div>
                        <h3 className="font-display font-semibold text-bl text-base">
                            {label} — recent rejections
                        </h3>
                        <p className="text-xs text-bl-muted mt-0.5">
                            Last 7 days · most recent 25
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="ce-health-failures-modal-close"
                        className="p-1.5 rounded hover:bg-bl-bg transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4 text-bl-muted" />
                    </button>
                </div>

                {/* Retry action bar */}
                <div className="px-5 py-3 border-b border-bl flex items-center justify-between gap-3 bg-bl-bg/30">
                    <div className="text-xs text-bl-muted">
                        {data
                            ? `${data.unique_packages_count} affected package${data.unique_packages_count === 1 ? "" : "s"} · ${data.events_count} rejection event${data.events_count === 1 ? "" : "s"}`
                            : "Loading…"}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => handleRetryTopN(3)}
                            disabled={retrying || !data?.unique_packages_count}
                            data-testid="ce-health-retry-top-3"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-bl-primary text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                        >
                            {retrying ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <PlayCircle className="h-3.5 w-3.5" />
                            )}
                            Retry top 3
                        </button>
                        <button
                            type="button"
                            onClick={() => handleRetryTopN(10)}
                            disabled={retrying || !data?.unique_packages_count}
                            data-testid="ce-health-retry-top-10"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-bl bg-white text-xs font-medium text-bl hover:bg-[var(--bl-teal-tint)]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Retry top 10
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto px-5 py-4">
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-bl-muted py-8 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading failures…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                            <XCircle className="h-4 w-4 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {!loading && !error && data && data.events_count === 0 && (
                        <div className="text-sm text-bl-muted py-8 text-center" data-testid="ce-health-failures-empty">
                            No {label} rejection events in the last 7 days. Healthy!
                        </div>
                    )}
                    {!loading && !error && data && data.events_count > 0 && (
                        <div className="space-y-2" data-testid="ce-health-failures-list">
                            {data.events.map((e) => (
                                <FailureRow key={e.id} event={e} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ─────────── Failure row with prompt-diff expander ───────────

/** Renders a single rejection event with an inline "View payload" expander
 * that shows the LAST payload the LLM returned (truncated to ~4KB). Gives
 * admins the missing "WHY did this fail" signal — instead of retrying
 * blindly, they can eyeball what the LLM actually produced and tune the
 * prompt accordingly. v4.38. */
function FailureRow({ event }) {
    const [expanded, setExpanded] = useState(false);
    const hasPayload = !!event.payload_preview;

    // Try to pretty-print the JSON preview.
    let prettyPayload = event.payload_preview || "";
    try {
        if (event.payload_preview) {
            prettyPayload = JSON.stringify(
                JSON.parse(event.payload_preview), null, 2,
            );
        }
    } catch {
        // Not valid JSON — show as-is.
    }

    return (
        <div
            className="border border-bl/40 rounded-md overflow-hidden"
            data-testid={`ce-health-failure-${event.id}`}
        >
            <div className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-bl text-sm truncate">
                            {event.topic_title || event.slug || event.package_id || "—"}
                        </div>
                        <div className="mt-0.5 text-xs text-[var(--bl-rust)]">
                            {event.reason || "malformed output"}
                        </div>
                        {Array.isArray(event.payload_keys) && event.payload_keys.length > 0 && (
                            <div className="mt-1 text-[10px] text-bl-muted">
                                Returned keys:{" "}
                                <span className="font-mono-bl text-[10px]">
                                    {event.payload_keys.join(", ")}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-bl-muted font-mono-bl">
                            {(event.created_at || "").slice(0, 19).replace("T", " ")}
                        </div>
                        {hasPayload && (
                            <button
                                type="button"
                                onClick={() => setExpanded((v) => !v)}
                                data-testid={`ce-health-failure-expand-${event.id}`}
                                className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] uppercase tracking-[0.14em] font-medium text-bl-muted hover:text-bl hover:bg-bl-bg transition-colors"
                                title={expanded ? "Hide LLM payload" : "View LLM payload"}
                            >
                                {expanded ? (
                                    <ChevronDown className="h-3 w-3" />
                                ) : (
                                    <ChevronRight className="h-3 w-3" />
                                )}
                                <Code className="h-3 w-3" />
                                Payload
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {expanded && hasPayload && (
                <div
                    className="border-t border-bl/40 bg-bl-bg/40 px-3 py-2"
                    data-testid={`ce-health-failure-payload-${event.id}`}
                >
                    <div className="text-[10px] uppercase tracking-[0.16em] text-bl-muted font-medium mb-1.5">
                        LLM returned ({event.payload_type || "object"})
                    </div>
                    <pre className="text-[11px] leading-snug text-bl font-mono-bl whitespace-pre-wrap break-all max-h-72 overflow-auto bg-white border border-bl/30 rounded p-2.5">
                        {prettyPayload}
                    </pre>
                    <div className="mt-1.5 text-[10px] text-bl-muted leading-relaxed">
                        Compare this against what the generator expected — empty fields,
                        wrong keys, or truncated JSON suggest the prompt needs tuning
                        (more explicit schema, lower temperature, or a clearer one-shot example).
                    </div>
                </div>
            )}
        </div>
    );
}

